<script lang="ts">
/**
 * Two level breadcrumb displayed on the left of the top bar.
 */
export interface HzSuggestion {
  /** Stable identifier handed back when the entry is chosen. */
  id: string;
  /** Two letters shown in the round chip. */
  initiales: string;
  /** Main line, truncated with an ellipsis when too long. */
  libelle: string;
  /** Secondary line, for instance "Client · Équilibré · KYC à jour". */
  detail: string;
  /** Chip tone: a client uses the primary tint, a product the neutral one. */
  genre: 'client' | 'produit';
}

export interface HzBreadcrumb {
  /** Parent module label, rendered muted. */
  parent: string;
  /** Current screen label, rendered strong. Omitted on module roots. */
  current?: string;
}
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    breadcrumb: HzBreadcrumb;
    /** Unread notification count. Zero hides the badge. */
    notifications?: number;
    /** Two letter initials of the signed in advisor. */
    userInitials: string;
    /** Current value of the global search field. */
    search?: string;
    /** Entries proposed under the field; at most eight, as in the design reference. */
    suggestions?: readonly HzSuggestion[];
  }>(),
  { notifications: 0, search: '', suggestions: () => [] },
);

const emit = defineEmits<{
  'update:search': [value: string];
  /** Raised when the notification bell is activated. */
  notifications: [];
  /** Raised when the avatar is activated. */
  account: [];
  /** Raised with the identifier of the chosen suggestion. */
  choisir: [suggestion: HzSuggestion];
}>();

const indice = ref(0);
const ouvert = computed(() => focused.value && props.search.trim().length >= 2);

/** Arrow keys walk the list, Enter picks, Escape closes without choosing. */
function surClavier(evenement: KeyboardEvent): void {
  if (!ouvert.value) return;
  const total = props.suggestions.length;
  if (evenement.key === 'ArrowDown' && total > 0) {
    evenement.preventDefault();
    indice.value = (indice.value + 1) % total;
  } else if (evenement.key === 'ArrowUp' && total > 0) {
    evenement.preventDefault();
    indice.value = (indice.value - 1 + total) % total;
  } else if (evenement.key === 'Enter' && total > 0) {
    evenement.preventDefault();
    const choix = props.suggestions[indice.value];
    if (choix !== undefined) choisir(choix);
  } else if (evenement.key === 'Escape') {
    field.value?.blur();
  }
}

function choisir(s: HzSuggestion): void {
  emit('choisir', s);
  emit('update:search', '');
  indice.value = 0;
  field.value?.blur();
}

const field = ref<HTMLInputElement | null>(null);
const focused = ref(false);

const badge = computed(() => (props.notifications > 99 ? '99+' : String(props.notifications)));
const bellLabel = computed(() =>
  props.notifications > 0 ? `Notifications, ${props.notifications} non lues` : 'Notifications',
);

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

function onShortcut(event: KeyboardEvent): void {
  if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
  if (isTypingTarget(event.target)) return;
  event.preventDefault();
  field.value?.focus();
}

function onInput(event: Event): void {
  indice.value = 0;
  emit('update:search', (event.target as HTMLInputElement).value);
}

onMounted(() => window.addEventListener('keydown', onShortcut));
onBeforeUnmount(() => window.removeEventListener('keydown', onShortcut));
</script>

<template>
  <header class="hz-topbar">
    <nav
      class="hz-topbar__crumbs"
      aria-label="Fil d’Ariane"
    >
      <span class="hz-topbar__parent">{{ breadcrumb.parent }}</span>
      <template v-if="breadcrumb.current">
        <span
          class="hz-topbar__sep"
          aria-hidden="true"
        >&rsaquo;</span>
        <span
          class="hz-topbar__current"
          aria-current="page"
        >{{ breadcrumb.current }}</span>
      </template>
    </nav>

    <div class="hz-topbar__search">
      <svg
        class="hz-topbar__glass"
        viewBox="0 0 15 15"
        aria-hidden="true"
        focusable="false"
      >
        <circle
          cx="6.4"
          cy="6.4"
          r="4.7"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
        />
        <line
          x1="10"
          y1="10"
          x2="13.9"
          y2="13.9"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
      <input
        ref="field"
        class="hz-topbar__input"
        type="search"
        :value="search"
        aria-label="Recherche globale"
        aria-keyshortcuts="/"
        placeholder="Rechercher un client, un produit..."
        @input="onInput"
        @focus="focused = true"
        @blur="focused = false"
        @keydown="surClavier"
      >
    </div>

    <div
      v-if="ouvert"
      class="hz-topbar__suggestions"
      role="listbox"
      aria-label="Résultats de la recherche"
    >
      <button
        v-for="(s, i) in props.suggestions"
        :key="s.id"
        type="button"
        role="option"
        class="hz-topbar__suggestion"
        :class="{ 'hz-topbar__suggestion--active': i === indice }"
        :aria-selected="i === indice"
        @mousedown.prevent="choisir(s)"
        @mouseenter="indice = i"
      >
        <span
          class="hz-topbar__chip"
          :class="`hz-topbar__chip--${s.genre}`"
          aria-hidden="true"
        >{{ s.initiales }}</span>
        <span class="hz-topbar__texte">
          <span class="hz-topbar__libelle">{{ s.libelle }}</span>
          <span class="hz-topbar__detail">{{ s.detail }}</span>
        </span>
      </button>
      <p
        v-if="props.suggestions.length === 0"
        class="hz-topbar__vide"
      >
        Aucun résultat pour cette recherche
      </p>
    </div>

    <div class="hz-topbar__actions">
      <button
        type="button"
        class="hz-topbar__bell"
        :aria-label="bellLabel"
        @click="emit('notifications')"
      >
        <svg
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M10 2.6 C 6.9 2.6 5.2 5 5.2 7.8 L 5.2 11.2 L 3.8 13.6 L 16.2 13.6 L 14.8 11.2 L 14.8 7.8 C 14.8 5 13.1 2.6 10 2.6 Z"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linejoin="round"
          />
          <path
            d="M8.3 15.8 C 8.6 16.9 9.2 17.4 10 17.4 C 10.8 17.4 11.4 16.9 11.7 15.8"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
        <span
          v-if="props.notifications > 0"
          class="hz-topbar__badge"
        >{{ badge }}</span>
      </button>
      <button
        type="button"
        class="hz-topbar__avatar"
        aria-label="Compte utilisateur"
        @click="emit('account')"
      >
        {{ userInitials }}
      </button>
    </div>
  </header>
</template>

<style scoped>
.hz-topbar {
  position: relative;
  z-index: 35;
  box-sizing: border-box;
  height: var(--hz-topbar-h);
  /* The height is pinned above, so the bar never needs to grow vertically. It must fill
     its row though: the search is positioned from the centre of this element and the
     actions are pushed to its right edge, so a shrunk bar misplaces both. */
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 var(--hz-sp-5);
  background: var(--hz-bg-card);
  font-family: var(--hz-font);
}

.hz-topbar__crumbs {
  display: flex;
  align-items: center;
  min-width: 0;
}

.hz-topbar__parent {
  font-size: 13px;
  color: var(--hz-text-muted);
}

.hz-topbar__sep {
  margin: 0 7px;
  font-size: 13px;
  color: var(--hz-text-muted);
}

.hz-topbar__current {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--hz-text-strong);
  white-space: nowrap;
}

.hz-topbar__search {
  position: absolute;
  left: 50%;
  transform: translateX(-63%);
  box-sizing: border-box;
  width: 320px;
  height: 34px;
  display: flex;
  align-items: center;
  gap: var(--hz-sp-2);
  padding: 0 14px;
  border: 1px solid var(--hz-border);
  border-radius: var(--hz-radius-pill);
  background: var(--hz-bg-card);
}

.hz-topbar__search:focus-within {
  box-shadow: var(--hz-focus);
}

.hz-topbar__glass {
  width: 15px;
  height: 15px;
  flex: none;
  color: var(--hz-text-muted);
}

.hz-topbar__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  color: var(--hz-text-strong);
}

.hz-topbar__input::placeholder {
  color: var(--hz-text-muted);
}

.hz-topbar__suggestions {
  position: absolute;
  top: 49px;
  left: 50%;
  transform: translateX(-63%);
  z-index: 60;
  box-sizing: border-box;
  width: 320px;
  padding: 6px;
  border: 1px solid var(--hz-border);
  border-radius: 10px;
  background: var(--hz-bg-card);
  box-shadow: var(--hz-shadow-pop);
}

.hz-topbar__suggestion {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 8px;
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.hz-topbar__suggestion--active {
  background: var(--hz-bg-app);
}

.hz-topbar__chip {
  width: 28px;
  height: 28px;
  flex: none;
  display: grid;
  place-items: center;
  border-radius: var(--hz-radius-pill);
  font-size: 11px;
  font-weight: 700;
}

.hz-topbar__chip--client {
  background: var(--hz-primary-100);
  color: var(--hz-primary-700);
}

.hz-topbar__chip--produit {
  background: var(--hz-neutral-bg);
  color: var(--hz-neutral-fg);
}

.hz-topbar__texte {
  flex: 1;
  min-width: 0;
}

.hz-topbar__libelle {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--hz-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hz-topbar__detail {
  display: block;
  font-size: 11px;
  color: var(--hz-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hz-topbar__vide {
  margin: 0;
  padding: 10px 8px;
  font-size: 12.5px;
  color: var(--hz-text-muted);
}

.hz-topbar__kbd {
  flex: none;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: grid;
  place-items: center;
  border: 1px solid var(--hz-border);
  border-radius: 5px;
  background: var(--hz-bg-app);
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  color: var(--hz-text-muted);
}

.hz-topbar__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 18px;
}

.hz-topbar__bell {
  position: relative;
  display: inline-flex;
  padding: 2px;
  border: none;
  border-radius: var(--hz-radius);
  background: none;
  color: var(--hz-text-strong);
  cursor: pointer;
}

.hz-topbar__bell svg {
  width: 20px;
  height: 20px;
}

.hz-topbar__bell:focus-visible,
.hz-topbar__avatar:focus-visible {
  outline: none;
  box-shadow: var(--hz-focus);
}

.hz-topbar__badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  display: grid;
  place-items: center;
  border-radius: var(--hz-radius-pill);
  background: var(--hz-danger-fg);
  color: var(--hz-bg-card);
  font-size: 9px;
  font-weight: 700;
}

.hz-topbar__avatar {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: var(--hz-radius-pill);
  background: var(--hz-primary-900);
  color: var(--hz-bg-card);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
</style>
